import { useEffect, useRef, useState } from 'react';
import { BookmarkPlus, Mic, Save, Square } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageProvider';
import { useFitnessResource, notifyFitnessChanged } from '@/hooks/useFitnessResource';
import { useVoiceInputWhisper, type WhisperOwnerGuard } from '@/hooks/useVoiceInputWhisper';
import { createFitnessId } from '@/lib/fitnessId';
import { getFitnessGuidedCopy } from '@/lib/fitnessGuidedCopy';
import { savePersonalRoutine } from '@/lib/fitness/routineLibrary';
import { readRoutineProgression, type RoutineProgression } from '@/lib/fitness/routines';
import type { FreeWorkoutItem } from '@/lib/fitness/freeWorkoutBuilder';
import WorkoutNumberInput from './WorkoutNumberInput';

const readIdentity = async (_ownerId: string, assert: () => void) => { assert(); return true; };
export interface FitnessRoutineSaveProps {
  ownerId: string; items: readonly FreeWorkoutItem[]; routineId?: string; routineDayIndex?: number;
  initialName?: string; progression?: RoutineProgression; disabled?: boolean; onSaved?: (id: string, name: string, progression: RoutineProgression) => void;
}
export default function FitnessRoutineSave({ ownerId, items, routineId, routineDayIndex, initialName = '', progression, disabled, onSaved }: FitnessRoutineSaveProps) {
  const { lang, t } = useLanguage(), copy = getFitnessGuidedCopy(lang);
  const identity = useFitnessResource(readIdentity);
  const [name, setName] = useState(initialName);
  const [rule, setRule] = useState<RoutineProgression>(() => readRoutineProgression(progression));
  const [saving, setSaving] = useState(false), [error, setError] = useState(false);
  const [invalid, setInvalid] = useState<Set<string>>(() => new Set());
  const saveId = useRef(createFitnessId()), saveSignature = useRef(''), lock = useRef<symbol | null>(null);
  const mounted = useRef(false);
  const latestReview = useRef({ ownerId, items, routineId, routineDayIndex, initialName, progression, name, rule });
  latestReview.current = { ownerId, items, routineId, routineDayIndex, initialName, progression, name, rule };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; lock.current = null; };
  }, []);
  useEffect(() => {
    // A pending result belongs to the exact proposal the user saved, even when
    // another proposal uses the same owner, name or mounted Review component.
    lock.current = null; setSaving(false); setError(false);
  }, [ownerId, items, routineId, routineDayIndex, initialName, progression]);
  const voiceGuard = useRef<WhisperOwnerGuard | undefined>();
  const voice = useVoiceInputWhisper(lang, { surface: 'work' });
  const cancelVoice = voice.cancelListening;

  useEffect(() => {
    setName(initialName); setRule(readRoutineProgression(progression)); setError(false);
    setInvalid(new Set()); setSaving(false); lock.current = null; saveId.current = createFitnessId(); saveSignature.current = '';
    cancelVoice(); voiceGuard.current = undefined;
  }, [ownerId, initialName, progression, cancelVoice]);
  const ready = identity.status === 'ready' && identity.userId === ownerId;
  const captureVoice = async () => {
    if (voice.isListening) {
      const guard = voiceGuard.current, result = await voice.stopListening(guard);
      if (guard?.isCurrent() && result) setName(result.slice(0, 160));
      return;
    }
    const current = identity.capture();
    if (!ready || !current()) return;
    const guard = { ownerId, isCurrent: current }; voiceGuard.current = guard;
    await voice.startListening(guard);
  };
  const save = async (update: boolean) => {
    if (!ready || lock.current || voice.isListening || voice.isTranscribing || disabled || !items.length || invalid.size || (!update && !name.trim())) return;
    const ownerCurrent = identity.capture(); if (!ownerCurrent()) return;
    const reviewed = latestReview.current, operation = Symbol('routine-save');
    const current = () => {
      const latest = latestReview.current;
      return mounted.current && ownerCurrent() && lock.current === operation
        && latest.ownerId === reviewed.ownerId && latest.items === reviewed.items
        && latest.routineId === reviewed.routineId && latest.routineDayIndex === reviewed.routineDayIndex
        && latest.initialName === reviewed.initialName && latest.progression === reviewed.progression
        && latest.name === reviewed.name && latest.rule === reviewed.rule;
    };
    lock.current = operation; setSaving(true); setError(false);
    try {
      if (!update) {
        const signature = JSON.stringify({ name: name.trim(), items, rule });
        if (saveSignature.current !== signature) { saveId.current = createFitnessId(); saveSignature.current = signature; }
      }
      const id = await savePersonalRoutine({ ownerId, name: name.trim(), items, progression: rule,
        ...(update && routineId ? { planId: routineId, dayIndex: routineDayIndex ?? 0 } : { newPlanId: saveId.current }) }, current);
      if (!current()) return;
      notifyFitnessChanged(); toast.success(update ? copy.updateOk : copy.savedOk); onSaved?.(id, update ? initialName : name.trim(), rule);
    } catch (cause) {
      console.warn('[FitnessRoutine] Save not confirmed', cause instanceof Error ? cause.message : 'unknown');
      if (current()) setError(true);
    } finally {
      if (lock.current === operation) {
        // Check the reviewed operation while its lock still belongs to it.
        if (current()) setSaving(false);
        lock.current = null;
      }
    }
  };
  const markValidity = (key: string, valid: boolean) => setInvalid(previous => {
    const next = new Set(previous); if (valid) next.delete(key); else next.add(key); return next;
  });
  return <section className="mt-6 rounded-2xl border border-primary/20 bg-primary/5 p-4">
    <p className="mb-4 text-sm text-muted-foreground">{routineId ? copy.today : copy.permanent}</p>
    <label className="block text-sm font-medium">
      {routineId ? copy.newRoutineName : copy.routineName}
      <span className="mt-2 flex items-center gap-2">
        <input value={name} maxLength={160} placeholder={copy.namePlaceholder} disabled={saving || disabled}
          onChange={event => setName(event.target.value)} className="min-h-11 min-w-0 flex-1 rounded-xl border border-border bg-background px-3 font-normal" />
        {voice.supported && <button type="button" disabled={!ready || saving || voice.isTranscribing || disabled}
          onClick={() => void captureVoice()} aria-label={t(voice.isListening ? 'journal.voice.stop' : 'journal.voice.start')}
          aria-pressed={voice.isListening} className="flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-border">
          {voice.isListening ? <Square size={16} /> : <Mic size={18} />}
        </button>}
      </span>
    </label>
    <details className="mt-4">
      <summary className="min-h-11 cursor-pointer py-3 text-sm font-medium">{copy.progression}</summary>
      <p className="mb-3 text-xs leading-relaxed text-muted-foreground">{copy.progressionBody}</p><p className="mb-3 text-xs leading-relaxed text-primary">{copy.progressionBaseline}</p>
      <label className="flex min-h-11 items-center gap-3 text-sm">
        <input type="checkbox" checked={rule.enabled} disabled={saving || disabled} onChange={event => { setRule(previous => ({ ...previous, enabled: event.target.checked })); setInvalid(new Set()); }} />
        {copy.enableProgression}
      </label>
      {rule.enabled && <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <WorkoutNumberInput label={copy.repsPerWeek} value={rule.repsPerWeek} min={0} max={5} integer disabled={saving || disabled} onChange={repsPerWeek => setRule(previous => ({ ...previous, repsPerWeek }))}
          onValidityChange={valid => markValidity('reps', valid)} />
        <WorkoutNumberInput label={copy.weightPerWeek} value={rule.weightKgPerWeek} min={0} max={20} disabled={saving || disabled} onChange={weightKgPerWeek => setRule(previous => ({ ...previous, weightKgPerWeek }))}
          onValidityChange={valid => markValidity('weight', valid)} />
        <WorkoutNumberInput label={copy.weeks} value={rule.maxWeeks} min={1} max={12} integer disabled={saving || disabled} onChange={maxWeeks => setRule(previous => ({ ...previous, maxWeeks }))}
          onValidityChange={valid => markValidity('weeks', valid)} />
      </div>}
    </details>
    {error && <p role="alert" className="mt-3 text-sm text-destructive">{copy.saveError}</p>}
    <div className="mt-4 flex flex-wrap gap-2">
      <button type="button" onClick={() => void save(false)} disabled={!ready || saving || voice.isListening || voice.isTranscribing || disabled || !items.length || !name.trim() || invalid.size > 0}
        className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-primary/40 px-3 py-3 text-sm font-medium text-primary disabled:opacity-40">
        <BookmarkPlus size={16} />{copy.save}
      </button>
      {routineId && <button type="button" onClick={() => void save(true)} disabled={!ready || saving || voice.isListening || voice.isTranscribing || disabled || !items.length || invalid.size > 0}
        className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-3 py-3 text-sm font-medium text-primary-foreground disabled:opacity-40">
        <Save size={16} />{copy.update}
      </button>}
    </div>
  </section>;
}
